import '@mappedin/mappedin-js/lib/esm/index.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import type maplibregl from 'maplibre-gl';
import type { ChangeEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FloatingIcons } from './components/FloatingIcons';
import { NotEmbeddedError } from './components/NotEmbeddedError';
import { TransitList } from './components/TransitList';
import { useVenueOS } from './hooks/useVenueOS';
import { useVenues } from './hooks/useVenues';
import { focusOnStop, loadVenueMap, recenterOnVenue } from './lib/map-helpers';
import type { MappedinMapView } from './lib/mappedin-types';
import { fetchTransitData } from './lib/transit-api';
import { TransitMarkerLayer } from './lib/transit-markers';
import type { TransitStop, Venue } from './types';

interface MapState {
  loading: boolean;
  error: string | null;
  mapView: MappedinMapView | null;
  mapLibreMap: maplibregl.Map | null;
  venueId: string | null;
}

interface TransitState {
  loading: boolean;
  error: string | null;
  stops: TransitStop[];
}

const INITIAL_MAP_STATE: MapState = {
  loading: false,
  error: null,
  mapView: null,
  mapLibreMap: null,
  venueId: null,
};
const INITIAL_TRANSIT_STATE: TransitState = { loading: false, error: null, stops: [] };

export function App(): ReactNode {
  const {
    client: venueOSClient,
    context: venueOSContext,
    isEmbedded,
    isLoading: venueOSLoading,
    error: venueOSError,
  } = useVenueOS();

  const {
    venues,
    isLoading: venuesLoading,
    error: venuesError,
  } = useVenues({ client: venueOSClient, context: venueOSContext });

  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);

  const [mapState, setMapState] = useState<MapState>(INITIAL_MAP_STATE);
  const [transitState, setTransitState] = useState<TransitState>(INITIAL_TRANSIT_STATE);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const markerLayerRef = useRef<TransitMarkerLayer | null>(null);
  const currentLoadRef = useRef<symbol | null>(null);

  // Auto-select when there's only one venue, mirroring web-applet behavior.
  useEffect(() => {
    if (venues.length === 1 && selectedVenueId === null) {
      setSelectedVenueId(venues[0]?.id ?? null);
    }
  }, [venues, selectedVenueId]);

  const selectedVenue = useMemo<Venue | null>(() => {
    if (!selectedVenueId) return null;
    return venues.find((v) => v.id === selectedVenueId) ?? null;
  }, [venues, selectedVenueId]);

  const selectedVenueKey = selectedVenue?.id ?? null;

  // Mount/teardown the map and load transit data when the selected venue changes.
  useEffect(() => {
    const container = mapContainerRef.current;
    const venue = selectedVenue;
    const client = venueOSClient;
    if (!venue || !container || !client) return undefined;

    const loadId = Symbol('venue-load');
    currentLoadRef.current = loadId;

    markerLayerRef.current?.detach();
    markerLayerRef.current = null;

    setMapState({
      loading: true,
      error: null,
      mapView: null,
      mapLibreMap: null,
      venueId: null,
    });
    setTransitState({ loading: true, error: null, stops: [] });
    setSelectedStopId(null);

    while (container.firstChild) container.removeChild(container.firstChild);

    let resolvedMapLibreMap: maplibregl.Map | null = null;

    const load = async (): Promise<void> => {
      let mapView: MappedinMapView | null = null;
      let mapLibreMap: maplibregl.Map | null = null;
      let mapError: string | null = null;

      try {
        const result = await loadVenueMap(client, container, venue);
        if (currentLoadRef.current !== loadId) {
          result.mapLibreMap.remove();
          return;
        }
        mapView = result.mapView;
        mapLibreMap = result.mapLibreMap;
        resolvedMapLibreMap = mapLibreMap;
      } catch (err: unknown) {
        if (currentLoadRef.current !== loadId) return;
        mapError = err instanceof Error ? err.message : 'Failed to load map';
      }

      if (currentLoadRef.current !== loadId) return;
      setMapState({
        loading: false,
        error: mapError,
        mapView,
        mapLibreMap,
        venueId: venue.id,
      });

      try {
        const { stops } = await fetchTransitData(client, venue.latitude, venue.longitude);
        if (currentLoadRef.current !== loadId) return;
        setTransitState({ loading: false, error: null, stops });

        if (mapView && stops.length > 0) {
          const layer = new TransitMarkerLayer();
          markerLayerRef.current = layer;
          layer.attach(
            mapView,
            mapLibreMap,
            stops,
            { latitude: venue.latitude, longitude: venue.longitude },
            venue.name,
          );
        }
      } catch (err: unknown) {
        if (currentLoadRef.current !== loadId) return;
        setTransitState({
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load transit data',
          stops: [],
        });
      }
    };

    void load();

    return () => {
      if (currentLoadRef.current === loadId) currentLoadRef.current = null;
      markerLayerRef.current?.detach();
      markerLayerRef.current = null;
      try {
        // Removing the MapLibre map invokes onRemove on every control,
        // including the Mappedin overlay, which tears down the MapView.
        resolvedMapLibreMap?.remove();
      } catch {
        // Already destroyed.
      }
    };
  }, [selectedVenue, selectedVenueKey, venueOSClient]);

  // Wire stop-click callbacks into the marker layer.
  useEffect(() => {
    const layer = markerLayerRef.current;
    if (!layer) return undefined;
    layer.setOnStopClick((stopId) => {
      setSelectedStopId((prev) => (prev === stopId ? null : stopId));
    });
    return () => {
      layer.setOnStopClick(null);
    };
  }, [mapState.mapView]);

  // Push highlight changes down to markers.
  useEffect(() => {
    markerLayerRef.current?.highlight(selectedStopId);
  }, [selectedStopId]);

  // Animate the camera in response to selection changes. The MapLibre map
  // owns the camera in the overlay architecture, so all flyTo / focus
  // operations target it directly.
  useEffect(() => {
    const mapLibreMap = mapState.mapLibreMap;
    if (!mapLibreMap || !selectedVenue) return;
    if (!selectedStopId) {
      recenterOnVenue(mapLibreMap, selectedVenue);
      return;
    }
    const stop = transitState.stops.find((s) => s.id === selectedStopId);
    if (stop) focusOnStop(mapLibreMap, stop.latitude, stop.longitude);
  }, [selectedStopId, mapState.mapLibreMap, selectedVenue, transitState.stops]);

  const handleStopSelect = useCallback((stop: TransitStop) => {
    setSelectedStopId((prev) => (prev === stop.id ? null : stop.id));
  }, []);

  const handleRecenter = useCallback(() => {
    setSelectedStopId(null);
  }, []);

  const handleVenueChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedVenueId(event.target.value);
  }, []);

  if (!isEmbedded) {
    return <NotEmbeddedError />;
  }

  if (venueOSLoading || venuesLoading) {
    return (
      <div className="fullscreen-center">
        <div className="spinner" />
        <span>Connecting to VenueOS...</span>
      </div>
    );
  }

  if (venueOSError || venuesError) {
    return (
      <div className="fullscreen-center">
        <span>{venueOSError ?? venuesError}</span>
      </div>
    );
  }

  if (venues.length === 0) {
    return <EmptyState />;
  }

  if (!selectedVenue) {
    return <VenuePicker venues={venues} onVenueChange={handleVenueChange} />;
  }

  return (
    <div className="app-layout">
      <TransitList
        stops={transitState.stops}
        selectedStopId={selectedStopId}
        onStopSelect={handleStopSelect}
        loading={transitState.loading}
        error={transitState.error}
      />

      <div className="map-panel">
        <div className="map-panel-header">
          <h1>Transit Info</h1>
          <span className="header-divider" />
          {venues.length > 1 ? (
            <div className="venue-selector">
              <select value={selectedVenueId ?? ''} onChange={handleVenueChange}>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <span className="venue-name">{selectedVenue.name}</span>
          )}
        </div>
        <div className="map-container">
          <div
            ref={mapContainerRef}
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
          />
          {!mapState.loading && !mapState.error && transitState.stops.length > 0 && (
            <button
              type="button"
              className="map-recenter-btn"
              onClick={handleRecenter}
              title="Reset view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M8 1v3M8 12v3M1 8h3M12 8h3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          {mapState.loading && (
            <div className="map-overlay">
              <div className="spinner" />
              <span>Loading map...</span>
            </div>
          )}
          {mapState.error && (
            <div className="map-overlay map-overlay-error">
              <span>Unable to load map</span>
              <span className="map-overlay-detail">
                Transit data is still shown in the sidebar.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState(): ReactNode {
  return (
    <div className="standalone-notice">
      <FloatingIcons />
      <div className="standalone-content">
        <h1>Transit Data Source</h1>
        <div className="steps">
          <div className="step">
            <span className="step-number">1</span>
            <span className="step-label">Select a venue</span>
            <span className="step-desc">Pick a venue address to scan for nearby transit</span>
          </div>
          <div className="step-connector" />
          <div className="step">
            <span className="step-number">2</span>
            <span className="step-label">Preview transit</span>
            <span className="step-desc">See bus, rail, bike share &amp; rideshare on the map</span>
          </div>
          <div className="step-connector" />
          <div className="step">
            <span className="step-number">3</span>
            <span className="step-label">Add as data source</span>
            <span className="step-desc">Publish transit data for use across all your apps</span>
          </div>
        </div>
        <p className="standalone-embed-hint">
          No venues with coordinates found. Add a venue to get started.
        </p>
      </div>
    </div>
  );
}

interface VenuePickerProps {
  venues: Venue[];
  onVenueChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

function VenuePicker({ venues, onVenueChange }: VenuePickerProps): ReactNode {
  return (
    <div className="fullscreen-center">
      <FloatingIcons />
      <div className="no-venue-prompt">
        <h3>Transit Data Source</h3>
        <div className="steps">
          <div className="step active">
            <span className="step-number">1</span>
            <span className="step-label">Select a venue</span>
            <span className="step-desc">Pick a venue from the selector below</span>
          </div>
          <div className="step-connector" />
          <div className="step">
            <span className="step-number">2</span>
            <span className="step-label">Preview transit</span>
            <span className="step-desc">See stops &amp; stations on the map</span>
          </div>
          <div className="step-connector" />
          <div className="step">
            <span className="step-number">3</span>
            <span className="step-label">Add as data source</span>
            <span className="step-desc">Share across all your apps</span>
          </div>
        </div>
        <div className="venue-picker-cta">
          <select value="" onChange={onVenueChange}>
            <option value="" disabled>
              Choose a venue...
            </option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
